import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Container, VStack, HStack, Heading, Text, Input, Button, Icon, Select,
  Badge, Spinner, Table, Thead, Tbody, Tr, Th, Td, Center, Stat, StatLabel,
  StatNumber, StatHelpText, SimpleGrid, InputGroup, InputLeftElement,
  useColorModeValue, IconButton, Tooltip, Link as ChakraLink,
} from '@chakra-ui/react';
import {
  FiSearch, FiRefreshCw, FiExternalLink, FiDatabase, FiChevronLeft,
  FiChevronRight, FiChevronUp, FiChevronDown, FiDollarSign, FiUsers, FiLink,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functionsUsEast1 } from '../utils/init-firebase';
import {
  getAriveStageLabel, getAriveStageColor, getAriveLeadStatusLabel,
  ARIVE_LOAN_STAGES, buildAriveDeepLink,
} from '../constants/ariveConstants';
import { useAuth } from '../contexts/AuthContext';

const PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

function emailPrefix(email) {
  if (!email) return null;
  return email.split('@')[0];
}

function teamMemberDisplay(name, email) {
  if (name) return name;
  return emailPrefix(email);
}

function formatCurrency(v) {
  if (v == null) return '--';
  return `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function formatDate(d) {
  if (!d) return '--';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return d;
  }
}

function timeAgo(d) {
  if (!d) return '--';
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(d);
}

function SortHeader({ label, field, sortField, sortDir, onSort, ...props }) {
  const active = sortField === field;
  const headerColor = useColorModeValue('gray.600', 'gray.300');
  const activeColor = useColorModeValue('blue.600', 'blue.300');

  return (
    <Th
      cursor="pointer"
      onClick={() => onSort(field)}
      userSelect="none"
      fontSize="10px"
      color={active ? activeColor : headerColor}
      _hover={{ color: activeColor }}
      {...props}
    >
      <HStack spacing={1}>
        <Text>{label}</Text>
        {active && (
          <Icon as={sortDir === 'asc' ? FiChevronUp : FiChevronDown} boxSize={3} />
        )}
      </HStack>
    </Th>
  );
}

function KpiCard({ label, value, helpText, icon, color }) {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Stat bg={bg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" px={5} py={4}>
      <HStack spacing={3}>
        <Box p={2} borderRadius="md" bg={useColorModeValue(`${color}.50`, `${color}.900`)}>
          <Icon as={icon} color={`${color}.500`} boxSize={5} />
        </Box>
        <Box>
          <StatLabel fontSize="xs" color="gray.500">{label}</StatLabel>
          <StatNumber fontSize="xl">{value}</StatNumber>
          {helpText && <StatHelpText fontSize="xs" mb={0}>{helpText}</StatHelpText>}
        </Box>
      </HStack>
    </Stat>
  );
}

export default function AriveLoansPage() {
  const { SuperAdmin, currentUser } = useAuth();

  // Company-sync visibility model: admins browse everything; everyone else
  // only sees mirror rows where their uid is in visible_user_ids (loan team
  // membership or linked-deal ownership).
  const isAdmin = SuperAdmin === true || currentUser?.CRMRole === 'CRM Admin';
  const uid = currentUser?.uid || null;

  const [loans, setLoans] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortField, setSortField] = useState('last_fetched_at');
  const [sortDir, setSortDir] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('');

  const [stats, setStats] = useState({ loans: 0, leads: 0, linked: 0, volume: 0 });
  const [statsLoading, setStatsLoading] = useState(true);

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const loadingOverlayBg = useColorModeValue('whiteAlpha.700', 'blackAlpha.500');
  const theadBg = useColorModeValue('gray.50', 'gray.700');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [entityFilter, stageFilter, pageSize]);

  // Service-role path: arive_loans is locked to backend after GA RLS.
  // One callable returns both the page of rows and the KPI stats.
  const fetchPage = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setStatsLoading(true);
    try {
      const fn = httpsCallable(functionsUsEast1, 'adminListAriveLoans');
      const result = await fn({
        page,
        pageSize,
        sortField,
        sortDir,
        entityFilter,
        stageFilter,
        search: debouncedSearch,
      });
      setLoans(result.data?.rows || []);
      setTotalCount(result.data?.count || 0);
      if (result.data?.stats) setStats(result.data.stats);
    } catch (err) {
      console.error('Failed to fetch Arive loans:', err);
      setLoans([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      setStatsLoading(false);
    }
  }, [page, pageSize, sortField, sortDir, entityFilter, stageFilter, debouncedSearch, isAdmin]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const handleSort = useCallback((field) => {
    setSortDir((prev) => (sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
    setSortField(field);
    setPage(0);
  }, [sortField]);

  const totalPages = Math.ceil(totalCount / pageSize);
  const rangeStart = page * pageSize + 1;
  const rangeEnd = Math.min((page + 1) * pageSize, totalCount);

  const stageOptions = useMemo(() =>
    ARIVE_LOAN_STAGES.map((s) => ({ value: s.value, label: s.label })),
  []);

  if (!isAdmin && !uid) {
    return (
      <Container maxW="5xl" py={20}>
        <Center>
          <VStack spacing={4}>
            <Heading size="md" color="red.500">Access Denied</Heading>
            <Text color={mutedColor}>You must be signed in to view Arive loans.</Text>
          </VStack>
        </Center>
      </Container>
    );
  }

  return (
    <Box bg={pageBg} minH="100vh">
      <Container maxW="8xl" py={6}>
        <VStack align="stretch" spacing={5}>
          {/* Header */}
          <HStack justify="space-between" flexWrap="wrap" gap={2}>
            <VStack align="start" spacing={0}>
              <HStack>
                <Icon as={FiDatabase} boxSize={6} color="blue.500" />
                <Heading size="lg">Arive Loans Database</Heading>
                {isAdmin && <Badge colorScheme="red" fontSize="xs" px={3} py={1}>ADMIN</Badge>}
              </HStack>
              <Text fontSize="sm" color={mutedColor}>
                {isAdmin
                  ? 'All Arive loans and leads synced company-wide'
                  : 'Arive loans and leads where you are on the loan team'}
              </Text>
            </VStack>
            <Button
              size="sm"
              leftIcon={<Icon as={FiRefreshCw} />}
              onClick={() => { fetchPage(); }}
              isLoading={loading}
              variant="outline"
            >
              Refresh
            </Button>
          </HStack>

          {/* KPI Stats */}
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
            <KpiCard
              label="Loans"
              value={statsLoading ? '...' : stats.loans.toLocaleString()}
              icon={FiDatabase}
              color="blue"
            />
            <KpiCard
              label="Leads"
              value={statsLoading ? '...' : stats.leads.toLocaleString()}
              icon={FiUsers}
              color="purple"
            />
            <KpiCard
              label="Linked to Origin"
              value={statsLoading ? '...' : stats.linked.toLocaleString()}
              helpText={!statsLoading && stats.loans + stats.leads > 0
                ? `${Math.round((stats.linked / (stats.loans + stats.leads)) * 100)}% of total`
                : undefined}
              icon={FiLink}
              color="green"
            />
            <KpiCard
              label="Total Loan Volume"
              value={statsLoading ? '...' : formatCurrency(stats.volume)}
              icon={FiDollarSign}
              color="teal"
            />
          </SimpleGrid>

          {/* Toolbar */}
          <Box bg={bg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" px={4} py={3}>
            <HStack spacing={3} flexWrap="wrap">
              <InputGroup maxW="320px" size="sm">
                <InputLeftElement>
                  <Icon as={FiSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search borrower, Arive ID, lender, originator..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>

              <Select
                size="sm"
                maxW="140px"
                value={entityFilter}
                onChange={(e) => setEntityFilter(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="loan">Loans</option>
                <option value="lead">Leads</option>
              </Select>

              <Select
                size="sm"
                maxW="200px"
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
              >
                <option value="">All Stages</option>
                {stageOptions.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </Select>

              <Text fontSize="xs" color={mutedColor} ml="auto">
                {totalCount.toLocaleString()} result{totalCount !== 1 ? 's' : ''}
              </Text>
            </HStack>
          </Box>

          {/* Table */}
          <Box
            bg={bg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            overflow="hidden"
          >
            <Box overflowX="auto" position="relative">
              {loading && (
                <Center
                  position="absolute" inset={0} bg={loadingOverlayBg}
                  zIndex={2} borderRadius="lg"
                >
                  <Spinner size="lg" color="blue.500" />
                </Center>
              )}

              <Table variant="simple" size="sm">
                <Thead bg={theadBg} position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <SortHeader label="Arive ID" field="arive_loan_id" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortHeader label="Type" field="entity_type" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortHeader label="Borrower" field="borrower_last_name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortHeader label="Stage" field="current_stage" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortHeader label="Amount" field="base_loan_amount" sortField={sortField} sortDir={sortDir} onSort={handleSort} isNumeric />
                    <Th fontSize="10px">Property</Th>
                    <Th fontSize="10px">Loan Team</Th>
                    <SortHeader label="Lender" field="lender_name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortHeader label="Revenue" field="net_loan_revenue" sortField={sortField} sortDir={sortDir} onSort={handleSort} isNumeric />
                    <Th fontSize="10px">Origin Deal</Th>
                    <SortHeader label="Last Synced" field="last_fetched_at" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  </Tr>
                </Thead>
                <Tbody>
                  {loans.length === 0 && !loading ? (
                    <Tr>
                      <Td colSpan={11}>
                        <Center py={10}>
                          <VStack spacing={2}>
                            <Icon as={FiDatabase} boxSize={8} color="gray.300" />
                            <Text color={mutedColor} fontSize="sm">
                              {debouncedSearch || entityFilter !== 'all' || stageFilter
                                ? 'No loans match your filters'
                                : 'No Arive loans imported yet'}
                            </Text>
                          </VStack>
                        </Center>
                      </Td>
                    </Tr>
                  ) : (
                    loans.map((loan) => {
                      const deepLink = buildAriveDeepLink(loan.entity_type, loan.arive_loan_id);
                      const property = [loan.property_city, loan.property_state].filter(Boolean).join(', ');
                      const borrowerName = [loan.borrower_first_name, loan.borrower_last_name].filter(Boolean).join(' ');
                      const deal = loan.crm_deals;
                      const lo = teamMemberDisplay(loan.originator_name, loan.originator_email);
                      const proc = teamMemberDisplay(loan.processor_name, loan.processor_email);
                      const loa = teamMemberDisplay(loan.loa_name, loan.loa_email);

                      return (
                        <Tr key={loan.id} _hover={{ bg: hoverBg }} transition="background 0.1s">
                          <Td fontSize="xs" fontWeight="medium">
                            <HStack spacing={1}>
                              <Text>{loan.arive_loan_id}</Text>
                              {deepLink && (
                                <Tooltip label="Open in Arive" fontSize="xs">
                                  <ChakraLink href={deepLink} isExternal>
                                    <Icon as={FiExternalLink} boxSize={3} color="blue.400" />
                                  </ChakraLink>
                                </Tooltip>
                              )}
                            </HStack>
                          </Td>
                          <Td>
                            <Badge
                              fontSize="10px"
                              colorScheme={loan.entity_type === 'loan' ? 'blue' : 'purple'}
                            >
                              {loan.entity_type === 'loan' ? 'Loan' : 'Lead'}
                            </Badge>
                          </Td>
                          <Td>
                            <VStack align="start" spacing={0}>
                              <Text fontSize="xs" fontWeight="medium" noOfLines={1}>
                                {borrowerName || '--'}
                              </Text>
                              {loan.borrower_email && (
                                <Text fontSize="10px" color={mutedColor} noOfLines={1}>
                                  {loan.borrower_email}
                                </Text>
                              )}
                            </VStack>
                          </Td>
                          <Td>
                            {loan.current_stage ? (
                              <Badge fontSize="10px" colorScheme={getAriveStageColor(loan.current_stage)}>
                                {loan.entity_type === 'lead'
                                  ? getAriveLeadStatusLabel(loan.current_stage)
                                  : getAriveStageLabel(loan.current_stage)}
                              </Badge>
                            ) : (
                              <Text fontSize="xs" color={mutedColor}>--</Text>
                            )}
                          </Td>
                          <Td isNumeric fontSize="xs">
                            {formatCurrency(loan.base_loan_amount)}
                          </Td>
                          <Td fontSize="xs" maxW="120px" noOfLines={1}>
                            {property || '--'}
                          </Td>
                          <Td>
                            <VStack align="start" spacing={0}>
                              {lo && (
                                <HStack spacing={1}>
                                  <Text fontSize="10px" color={mutedColor} minW="20px">LO</Text>
                                  <Text fontSize="xs" noOfLines={1}>{lo}</Text>
                                </HStack>
                              )}
                              {proc && (
                                <HStack spacing={1}>
                                  <Text fontSize="10px" color={mutedColor} minW="20px">LP</Text>
                                  <Text fontSize="xs" noOfLines={1}>{proc}</Text>
                                </HStack>
                              )}
                              {loa && (
                                <HStack spacing={1}>
                                  <Text fontSize="10px" color={mutedColor} minW="20px">LOA</Text>
                                  <Text fontSize="xs" noOfLines={1}>{loa}</Text>
                                </HStack>
                              )}
                              {!lo && !proc && !loa && (
                                <Text fontSize="xs" color={mutedColor}>--</Text>
                              )}
                            </VStack>
                          </Td>
                          <Td fontSize="xs" maxW="120px" noOfLines={1}>
                            {loan.lender_name || '--'}
                          </Td>
                          <Td isNumeric fontSize="xs">
                            {formatCurrency(loan.net_loan_revenue)}
                          </Td>
                          <Td>
                            {deal ? (
                              <Badge
                                as={Link}
                                to={`/crm/deals/${deal.id}`}
                                fontSize="10px"
                                colorScheme="green"
                                cursor="pointer"
                                _hover={{ textDecoration: 'underline' }}
                              >
                                {deal.deal_name || 'Linked'}
                              </Badge>
                            ) : (
                              <Text fontSize="10px" color={mutedColor}>Not linked</Text>
                            )}
                          </Td>
                          <Td>
                            <Tooltip label={formatDate(loan.last_fetched_at)} fontSize="xs">
                              <Text fontSize="xs" color={mutedColor}>
                                {timeAgo(loan.last_fetched_at)}
                              </Text>
                            </Tooltip>
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </Table>
            </Box>

            {/* Pagination */}
            {totalCount > 0 && (
              <HStack
                justify="space-between"
                px={4} py={3}
                borderTopWidth="1px"
                borderColor={borderColor}
                flexWrap="wrap"
                gap={2}
              >
                <HStack spacing={2}>
                  <Text fontSize="xs" color={mutedColor}>Rows per page:</Text>
                  <Select
                    size="xs"
                    w="70px"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    {PAGE_SIZES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </Select>
                </HStack>

                <HStack spacing={3}>
                  <Text fontSize="xs" color={mutedColor}>
                    {rangeStart}–{rangeEnd} of {totalCount.toLocaleString()}
                  </Text>
                  <HStack spacing={1}>
                    <IconButton
                      icon={<FiChevronLeft />}
                      size="xs"
                      variant="ghost"
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      isDisabled={page === 0}
                      aria-label="Previous page"
                    />
                    <Text fontSize="xs" color={mutedColor} minW="60px" textAlign="center">
                      Page {page + 1} of {totalPages}
                    </Text>
                    <IconButton
                      icon={<FiChevronRight />}
                      size="xs"
                      variant="ghost"
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                      isDisabled={page >= totalPages - 1}
                      aria-label="Next page"
                    />
                  </HStack>
                </HStack>
              </HStack>
            )}
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
